import React, { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { taskDefinitions, nofrCategories } from '../data/competitionData';
import { staggerContainer, listItem, fadeIn } from '../styles/animations';

// Styled components for the score breakdown
const BreakdownContainer = styled(motion.div)`
  background: ${({ theme }) => theme.colors.bgLightGradient};
  border-radius: ${({ theme }) => theme.radii.lg};
  padding: ${({ theme }) => theme.space.lg};
  margin-top: ${({ theme }) => theme.space.lg};
  border: 1px solid ${({ theme }) => theme.colors.border};
  position: relative;
  overflow: hidden;
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(to right, ${({ theme }) => theme.colors.primary}, ${({ theme }) => theme.colors.secondary});
  }
`;

const BreakdownHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.space.md};
`;

const BreakdownTitle = styled.h3`
  font-family: ${({ theme }) => theme.fonts.heading};
  font-size: ${({ theme }) => theme.fontSizes.lg};
  color: ${({ theme }) => theme.colors.textPrimary};
  margin: 0;
`;

const ToggleButton = styled.button`
  background: transparent;
  border: 1px solid ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.primary};
  padding: ${({ theme }) => theme.space.xs} ${({ theme }) => theme.space.sm};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  border-radius: ${({ theme }) => theme.radii.sm};
  cursor: pointer;
  transition: ${({ theme }) => theme.transitions.fast};
  
  &:hover {
    background: rgba(0, 212, 255, 0.1);
    box-shadow: none;
  }
`;

const SectionTitle = styled.h4`
  font-family: ${({ theme }) => theme.fonts.heading};
  font-size: ${({ theme }) => theme.fontSizes.md};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: ${({ theme }) => theme.space.md} 0 ${({ theme }) => theme.space.sm};
  padding-bottom: ${({ theme }) => theme.space.xs};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const ScoreList = styled(motion.ul)`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const ScoreItem = styled(motion.li)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${({ theme }) => theme.space.xs} 0;
  border-bottom: 1px dashed rgba(255, 255, 255, 0.1);
  
  &:last-child {
    border-bottom: none;
  }
`;

const TaskName = styled.div`
  font-family: ${({ theme }) => theme.fonts.body};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  color: ${({ theme }) => theme.colors.textPrimary};
  display: flex;
  align-items: center;
  
  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: ${({ theme }) => theme.fontSizes.md};
  }
`;

const TaskId = styled.span`
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: ${({ theme }) => theme.fontSizes.xs};
  color: ${({ theme }) => theme.colors.primary};
  background: rgba(0, 212, 255, 0.1);
  padding: 2px 6px;
  border-radius: ${({ theme }) => theme.radii.sm};
  margin-right: ${({ theme }) => theme.space.sm};
  min-width: 60px;
  text-align: center;
  
  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: ${({ theme }) => theme.fontSizes.sm};
    min-width: 70px;
  }
`;

const ScoreDisplay = styled.div`
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  color: ${({ theme }) => theme.colors.textPrimary};
  display: flex;
  align-items: center;
  
  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: ${({ theme }) => theme.fontSizes.md};
  }
`;

const EarnedScore = styled.span`
  color: ${({ theme, isMax }) => isMax ? theme.colors.accent2 : theme.colors.textPrimary};
`;

const MaxScore = styled.span`
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 0.85em;
`;

const NofrScore = styled.span`
  color: ${({ theme, rank }) => {
    switch (rank) {
      case 1:
        return theme.colors.gold;
      case 2:
        return theme.colors.silver;
      case 3:
        return theme.colors.bronze;
      default:
        return theme.colors.textPrimary;
    }
  }};
`;

const TotalScoreRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: ${({ theme }) => theme.space.md};
  padding-top: ${({ theme }) => theme.space.sm};
  border-top: 2px solid ${({ theme }) => theme.colors.border};
  font-family: ${({ theme }) => theme.fonts.heading};
  font-size: ${({ theme }) => theme.fontSizes.lg};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
`;

const TotalLabel = styled.span`
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const TotalValue = styled.span`
  color: ${({ theme }) => theme.colors.primary};
  font-family: ${({ theme }) => theme.fonts.mono};
`;

/**
 * ScoreBreakdown Component
 * Displays detailed breakdown of a team's scores for AIDO tasks and NOFR categories
 * 
 * @param {Object} props - Component props
 * @param {Object} props.team - Team data object
 * @param {boolean} props.animate - Whether to animate the component
 */
const ScoreBreakdown = ({ team, animate = true }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };
  
  return (
    <BreakdownContainer
      initial={animate ? "hidden" : "visible"}
      animate="visible"
      variants={fadeIn}
    >
      <BreakdownHeader>
        <BreakdownTitle>{team.name} Score Breakdown</BreakdownTitle>
        <ToggleButton onClick={toggleExpanded}>
          {isExpanded ? 'Hide Details' : 'Show Details'}
        </ToggleButton>
      </BreakdownHeader>
      
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            {/* AIDO Tasks Section */}
            <SectionTitle>AIDO Tasks</SectionTitle>
            <ScoreList
              variants={staggerContainer}
              initial="hidden"
              animate="visible"
            >
              {Object.entries(team.aidoScores).map(([taskId, score]) => (
                <ScoreItem key={taskId} variants={listItem}>
                  <TaskName>
                    <TaskId>{taskId}</TaskId>
                    {taskDefinitions[taskId]?.name || 'Unknown Task'}
                  </TaskName>
                  <ScoreDisplay>
                    <EarnedScore isMax={score.earned === score.max}>
                      {score.earned}
                    </EarnedScore>
                    <MaxScore> / {score.max}</MaxScore>
                  </ScoreDisplay>
                </ScoreItem>
              ))}
            </ScoreList>
            
            {/* NOFR Scores Section */}
            <SectionTitle>Non-Functional Requirements</SectionTitle>
            <ScoreList
              variants={staggerContainer}
              initial="hidden"
              animate="visible"
            >
              {Object.entries(team.nofrScores).map(([category, score]) => (
                <ScoreItem key={category} variants={listItem}>
                  <TaskName>
                    <TaskId>NOFR</TaskId>
                    {nofrCategories[category]?.name || category}
                  </TaskName>
                  <ScoreDisplay>
                    <NofrScore rank={team.rank}>
                      {score} pts
                    </NofrScore>
                  </ScoreDisplay>
                </ScoreItem>
              ))}
            </ScoreList>
            
            {/* Total Score */}
            <TotalScoreRow>
              <TotalLabel>TOTAL SCORE</TotalLabel>
              <TotalValue>{team.totalScore} pts</TotalValue>
            </TotalScoreRow>
          </motion.div>
        )}
      </AnimatePresence>
    </BreakdownContainer>
  );
};

export default ScoreBreakdown;